import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  Clock, Calendar as CalendarIcon, Plus, Trash2, Edit2, Users, 
  Sun, Coffee, LogOut, AlertTriangle, Plane, MapPin, Save, RefreshCw,
  ChevronLeft, ChevronRight, Check, X, Bell, Stethoscope, User, Download
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_TYPES = [
  { value: 'entrada', label: 'Entrada', icon: Sun, color: 'bg-green-100 text-green-800' },
  { value: 'salida_comer', label: 'Salida a comer', icon: Coffee, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'entrada_comer', label: 'Vuelta de comer', icon: Coffee, color: 'bg-blue-100 text-blue-800' },
  { value: 'salida_medico', label: 'Salida Médico', icon: Stethoscope, color: 'bg-pink-100 text-pink-800' },
  { value: 'entrada_medico', label: 'Entrada Médico', icon: Stethoscope, color: 'bg-pink-100 text-pink-800' },
  { value: 'salida_personal', label: 'Salida Asuntos', icon: User, color: 'bg-orange-100 text-orange-800' },
  { value: 'entrada_personal', label: 'Entrada Asuntos', icon: User, color: 'bg-orange-100 text-orange-800' },
  { value: 'salida', label: 'Fin de jornada', icon: LogOut, color: 'bg-red-100 text-red-800' },
  { value: 'incidencia', label: 'Incidencia', icon: AlertTriangle, color: 'bg-purple-100 text-purple-800' },
];

const INCIDENCIA_TYPES = [
  { value: 'medico', label: 'Médico' },
  { value: 'personal', label: 'Personal' },
  { value: 'otro', label: 'Otro' },
];

const COMUNIDADES = [
  "Andalucía", "Aragón", "Asturias", "Baleares", "Canarias", "Cantabria",
  "Castilla-La Mancha", "Castilla y León", "Cataluña", "Comunidad Valenciana",
  "Extremadura", "Galicia", "La Rioja", "Madrid", "Murcia", "Navarra", "País Vasco", "Ceuta", "Melilla"
];

const TimeTrackingTab = ({ isSuperadmin: propIsSuperadmin, currentUser: propCurrentUser }) => {
  const { token, user: authUser, isSuperadmin: authIsSuperadmin } = useAuth();
  const { language } = useLanguage();
  
  // Use props if provided, otherwise fallback to auth context
  const isSuperadmin = propIsSuperadmin !== undefined ? propIsSuperadmin : authIsSuperadmin;
  const currentUser = propCurrentUser || authUser;
  
  // State
  const [activeTab, setActiveTab] = useState('fichajes');
  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Time entries state
  const [entries, setEntries] = useState([]);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    worker_id: '',
    entry_type: 'entrada',
    timestamp: '',
    notes: '',
    incidencia_type: ''
  });
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    date: '',
    name: '',
    type: 'nacional',
    comunidad: '',
    worker_id: ''
  });
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  
  // Vacations state
  const [vacations, setVacations] = useState([]);
  const [vacationDays, setVacationDays] = useState([]);
  const [selectedVacationDates, setSelectedVacationDates] = useState([]);
  const [vacationMonth, setVacationMonth] = useState(new Date());
  
  // Schedules state (configuration)
  const [schedules, setSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // Edit vacation days state
  const [editingVacationWorker, setEditingVacationWorker] = useState(null);
  const [vacationTotalDays, setVacationTotalDays] = useState(22);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportWorker, setExportWorker] = useState('all');
  
  // Festivos por trabajador
  const [holidayWorkerFilter, setHolidayWorkerFilter] = useState('all');
  
  // Helper: Only superadmin can edit/delete records
  const canEdit = isSuperadmin;
  
  // Fetch workers
  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/workers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkers(response.data.workers);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, [token]);
  
  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedules(response.data.schedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, [token]);
  
  // Fetch time entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const params = new URLSearchParams();
      params.append('date_from', dateStr);
      params.append('date_to', dateStr);
      if (selectedWorker) params.append('worker_id', selectedWorker);
      
      const response = await axios.get(`${API}/admin/time-tracking/entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Error al cargar fichajes');
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate, selectedWorker]);
  
  // Fetch holidays
  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/holidays?year=${holidayYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  }, [token, holidayYear]);
  
  // Fetch vacations
  const fetchVacations = useCallback(async () => {
    try {
      const year = vacationMonth.getFullYear();
      const [vacResponse, daysResponse] = await Promise.all([
        axios.get(`${API}/admin/time-tracking/vacations?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/time-tracking/vacation-days?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVacations(vacResponse.data.vacations);
      setVacationDays(daysResponse.data.vacation_days);
    } catch (error) {
      console.error('Error fetching vacations:', error);
    }
  }, [token, vacationMonth]);
  
  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);
  
  useEffect(() => {
    if (activeTab === 'fichajes') fetchEntries();
    if (activeTab === 'festivos') fetchHolidays();
    if (activeTab === 'vacaciones') fetchVacations();
    if (activeTab === 'configuracion') fetchSchedules();
  }, [activeTab, fetchEntries, fetchHolidays, fetchVacations, fetchSchedules]);
  
  // Handle update schedule
  const handleUpdateSchedule = async (workerId, updates) => {
    try {
      await axios.post(`${API}/admin/time-tracking/schedules`, {
        worker_id: workerId,
        ...updates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración actualizada');
      fetchSchedules();
      setEditingSchedule(null);
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };
  
  // Export fichajes to CSV
  const handleExportFichajes = async (workerId, year, month) => {
    setExportLoading(true);
    try {
      const params = { year };
      if (month) params.month = month;
      if (workerId && workerId !== 'all') params.worker_id = workerId;
      
      const response = await axios.get(`${API}/admin/time-tracking/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const workerName = workerId === 'all' ? 'todos' : workers.find(w => w.id === workerId)?.name || workerId;
      const filename = month 
        ? `fichajes_${workerName}_${year}-${String(month).padStart(2, '0')}.csv`
        : `fichajes_${workerName}_${year}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Fichajes exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar fichajes');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handlers
  const handleCreateEntry = async () => {
    try {
      const data = {
        ...entryForm,
        timestamp: entryForm.timestamp ? new Date(entryForm.timestamp).toISOString() : new Date().toISOString()
      };
      
      if (editingEntry) {
        await axios.put(`${API}/admin/time-tracking/entries/${editingEntry.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fichaje actualizado');
      } else {
        await axios.post(`${API}/admin/time-tracking/entries`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fichaje registrado');
      }
      
      setShowEntryDialog(false);
      setEditingEntry(null);
      setEntryForm({ worker_id: '', entry_type: 'entrada', timestamp: '', notes: '', incidencia_type: '' });
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar fichaje');
    }
  };
  
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('¿Eliminar este fichaje?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fichaje eliminado');
      fetchEntries();
    } catch (error) {
      toast.error('Error al eliminar fichaje');
    }
  };
  
  const handleCreateHoliday = async () => {
    try {
      if (editingHoliday) {
        await axios.put(`${API}/admin/time-tracking/holidays/${editingHoliday.id}`, holidayForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Festivo actualizado');
      } else {
        await axios.post(`${API}/admin/time-tracking/holidays`, holidayForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Festivo creado');
      }
      
      setShowHolidayDialog(false);
      setEditingHoliday(null);
      setHolidayForm({ date: '', name: '', type: 'nacional', comunidad: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar festivo');
    }
  };
  
  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('¿Eliminar este festivo?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Festivo eliminado');
      fetchHolidays();
    } catch (error) {
      toast.error('Error al eliminar festivo');
    }
  };
  
  // Handle approve/reject vacation request
  const handleApproveVacation = async (dayId, approve, rejectReason = '') => {
    try {
      await axios.put(`${API}/admin/time-tracking/vacation-days/${dayId}/approve`, {
        approve,
        reject_reason: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(approve ? 'Vacaciones aprobadas' : 'Vacaciones rechazadas');
      fetchVacations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar solicitud');
    }
  };
  
  // Handle bulk approve/reject
  const handleBulkApprove = async (approve) => {
    const pendingDays = vacationDays.filter(d => d.status === 'pendiente');
    if (pendingDays.length === 0) {
      toast.error('No hay solicitudes pendientes');
      return;
    }
    
    const confirmMsg = approve 
      ? `¿Aprobar ${pendingDays.length} solicitud(es)?`
      : `¿Rechazar ${pendingDays.length} solicitud(es)?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.put(`${API}/admin/time-tracking/vacation-days/approve-bulk`, {
        day_ids: pendingDays.map(d => d.id),
        approve,
        reject_reason: approve ? '' : 'Solicitud rechazada por el administrador'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${pendingDays.length} solicitudes ${approve ? 'aprobadas' : 'rechazadas'}`);
      fetchVacations();
    } catch (error) {
      toast.error('Error al procesar solicitudes');
    }
  };
  
  const handleAddVacationDays = async () => {
    if (!selectedWorker || selectedVacationDates.length === 0) {
      toast.error('Selecciona un trabajador y al menos una fecha');
      return;
    }
    
    try {
      const dates = selectedVacationDates.map(d => format(d, 'yyyy-MM-dd'));
      await axios.post(`${API}/admin/time-tracking/vacation-days`, {
        worker_id: selectedWorker,
        dates,
        notes: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${dates.length} días de vacaciones añadidos`);
      setSelectedVacationDates([]);
      fetchVacations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir vacaciones');
    }
  };
  
  const handleDeleteVacationDay = async (dayId) => {
    if (!window.confirm('¿Eliminar este día de vacaciones?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/vacation-days/${dayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Día eliminado');
      fetchVacations();
    } catch (error) {
      toast.error('Error al eliminar día');
    }
  };
  
  // Update vacation total days
  const handleUpdateVacationDays = async () => {
    if (!editingVacationWorker) return;
    try {
      await axios.post(`${API}/admin/time-tracking/vacations`, {
        worker_id: editingVacationWorker.id,
        year: vacationMonth.getFullYear(),
        total_days: vacationTotalDays
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vacaciones actualizadas: ${vacationTotalDays} días`);
      setEditingVacationWorker(null);
      fetchVacations();
    } catch (error) {
      toast.error('Error al actualizar vacaciones');
    }
  };
  
  const getEntryTypeInfo = (type) => ENTRY_TYPES.find(t => t.value === type) || ENTRY_TYPES[0];
  
  // Calendar helpers for vacations
  const monthStart = startOfMonth(vacationMonth);
  const monthEnd = endOfMonth(vacationMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const isVacationDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return vacationDays.some(d => d.date === dateStr && (!selectedWorker || d.worker_id === selectedWorker));
  };
  
  const isHolidayDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.some(h => h.date === dateStr);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Control de Fichajes
          </h2>
          <p className="text-muted-foreground">Gestión de jornada laboral, festivos y vacaciones</p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="fichajes" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Fichajes
          </TabsTrigger>
          <TabsTrigger value="festivos" className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            Festivos
          </TabsTrigger>
          <TabsTrigger value="vacaciones" className="flex items-center gap-2">
            <Plane className="h-4 w-4" />
            Vacaciones
          </TabsTrigger>
          <TabsTrigger value="configuracion" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Configuración
          </TabsTrigger>
        </TabsList>
        
        {/* FICHAJES TAB */}
        <TabsContent value="fichajes" className="space-y-4">
          {/* Export section */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Exportar Fichajes</CardTitle>
                  <CardDescription>Descarga los fichajes en formato CSV</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={exportWorker} onValueChange={setExportWorker}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Trabajador" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los trabajadores</SelectItem>
                      {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExportFichajes(exportWorker, selectedDate.getFullYear(), selectedDate.getMonth() + 1)}
                    disabled={exportLoading}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {format(selectedDate, 'MMMM yyyy', { locale: es })}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleExportFichajes(exportWorker, selectedDate.getFullYear())}
                    disabled={exportLoading}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Año {selectedDate.getFullYear()}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="text-lg">Registro de Fichajes</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={selectedWorker || "all"} onValueChange={(v) => setSelectedWorker(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Todos los trabajadores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {workers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[200px] justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(selectedDate, 'PPP', { locale: es })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>
                  
                  {canEdit && (
                    <Button onClick={() => {
                      setEditingEntry(null);
                      setEntryForm({
                        worker_id: selectedWorker || '',
                        entry_type: 'entrada',
                        timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                        notes: '',
                        incidencia_type: ''
                      });
                      setShowEntryDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Fichaje
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Cargando...</div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay fichajes para esta fecha
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trabajador</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead>Editado por</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map(entry => {
                      const typeInfo = getEntryTypeInfo(entry.entry_type);
                      const Icon = typeInfo.icon;
                      return (
                        <TableRow key={entry.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{entry.worker_name}</p>
                              <p className="text-xs text-muted-foreground">{entry.worker_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={typeInfo.color}>
                              <Icon className="h-3 w-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                            {entry.incidencia_type && (
                              <span className="text-xs text-muted-foreground ml-2">
                                ({entry.incidencia_type})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {format(parseISO(entry.timestamp), 'HH:mm')}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {entry.notes || '-'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {entry.edited_by || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    setEntryForm({
                                      worker_id: entry.worker_id,
                                      entry_type: entry.entry_type,
                                      timestamp: format(parseISO(entry.timestamp), "yyyy-MM-dd'T'HH:mm"),
                                      notes: entry.notes || '',
                                      incidencia_type: entry.incidencia_type || ''
                                    });
                                    setShowEntryDialog(true);
                                  }}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* FESTIVOS TAB */}
        <TabsContent value="festivos" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="text-lg">Gestión de Festivos</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={String(holidayYear)} onValueChange={(v) => setHolidayYear(Number(v))}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canEdit && (
                    <Button onClick={() => {
                      setEditingHoliday(null);
                      setHolidayForm({ date: '', name: '', type: 'nacional', comunidad: '', worker_id: '' });
                      setShowHolidayDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Festivo
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Nacionales */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">Nacionales</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'nacional').length})</span>
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {holidays.filter(h => h.type === 'nacional').map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: h.comunidad || '', worker_id: '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'nacional').length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos nacionales</p>
                    )}
                  </div>
                </div>
                
                {/* Locales */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">Locales</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'local').length})</span>
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {holidays.filter(h => h.type === 'local').map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                          <Badge variant="outline" className="text-xs mt-1">
                            <MapPin className="h-2 w-2 mr-1" />
                            {h.comunidad}
                          </Badge>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: h.comunidad || '', worker_id: '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'local').length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos locales</p>
                    )}
                  </div>
                </div>
                
                {/* Personales por Trabajador */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800">Por Trabajador</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'personal').length})</span>
                  </h3>
                  <div className="mb-3">
                    <Select value={holidayWorkerFilter} onValueChange={setHolidayWorkerFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filtrar por trabajador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los trabajadores</SelectItem>
                        {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {holidays
                      .filter(h => h.type === 'personal')
                      .filter(h => holidayWorkerFilter === 'all' || h.worker_id === holidayWorkerFilter)
                      .map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                          <Badge variant="outline" className="text-xs mt-1">
                            <User className="h-2 w-2 mr-1" />
                            {h.worker_name || 'Trabajador'}
                          </Badge>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: '', worker_id: h.worker_id || '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'personal').filter(h => holidayWorkerFilter === 'all' || h.worker_id === holidayWorkerFilter).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos personales</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* VACACIONES TAB */}
        <TabsContent value="vacaciones" className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Calendario */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Calendario de Vacaciones</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setVacationMonth(new Date(vacationMonth.setMonth(vacationMonth.getMonth() - 1)))}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="font-medium min-w-[150px] text-center">
                      {format(vacationMonth, 'MMMM yyyy', { locale: es })}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setVacationMonth(new Date(vacationMonth.setMonth(vacationMonth.getMonth() + 1)))}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Select value={selectedWorker || "all"} onValueChange={(v) => setSelectedWorker(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[250px]">
                      <SelectValue placeholder="Seleccionar trabajador" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los trabajadores</SelectItem>
                      {workers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Empty cells for days before month start */}
                  {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-12" />
                  ))}
                  
                  {monthDays.map(day => {
                    const isVacation = isVacationDay(day);
                    const isHoliday = isHolidayDay(day);
                    const isSelected = selectedVacationDates.some(d => isSameDay(d, day));
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => {
                          if (selectedWorker && !isWeekend && !isHoliday) {
                            if (isSelected) {
                              setSelectedVacationDates(prev => prev.filter(d => !isSameDay(d, day)));
                            } else {
                              setSelectedVacationDates(prev => [...prev, day]);
                            }
                          }
                        }}
                        disabled={!selectedWorker || isWeekend}
                        className={`
                          h-12 rounded-lg text-sm font-medium transition-colors
                          ${isToday(day) ? 'ring-2 ring-primary' : ''}
                          ${isWeekend ? 'bg-gray-100 text-gray-400' : ''}
                          ${isHoliday ? 'bg-red-100 text-red-800' : ''}
                          ${isVacation ? 'bg-green-200 text-green-800' : ''}
                          ${isSelected ? 'bg-blue-500 text-white' : ''}
                          ${!isWeekend && !isHoliday && !isVacation && !isSelected ? 'hover:bg-gray-100' : ''}
                          disabled:cursor-not-allowed
                        `}
                      >
                        {format(day, 'd')}
                      </button>
                    );
                  })}
                </div>
                
                {selectedWorker && selectedVacationDates.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                    <span className="text-sm">
                      {selectedVacationDates.length} día(s) seleccionado(s)
                    </span>
                    <Button onClick={handleAddVacationDays}>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Vacaciones
                    </Button>
                  </div>
                )}
                
                <div className="mt-4 flex flex-wrap gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-green-200 rounded" />
                    <span>Vacaciones</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-red-100 rounded" />
                    <span>Festivo</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-gray-100 rounded" />
                    <span>Fin de semana</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 bg-blue-500 rounded" />
                    <span>Seleccionado</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Resumen de vacaciones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Resumen por Trabajador</CardTitle>
                <CardDescription>Haz clic en Editar para cambiar los días totales</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {workers.map(worker => {
                  const workerVacation = vacations.find(v => v.worker_id === worker.id && v.year === vacationMonth.getFullYear());
                  const totalDays = workerVacation?.total_days || 22;
                  
                  // Calculate from actual vacation days
                  const workerVacDays = vacationDays.filter(d => d.worker_id === worker.id);
                  const approvedDays = workerVacDays.filter(d => d.status === 'aprobado').length;
                  const pendingDays = workerVacDays.filter(d => d.status === 'pendiente').length;
                  const rejectedDays = workerVacDays.filter(d => d.status === 'rechazado').length;
                  const availableDays = totalDays - approvedDays - pendingDays;
                  
                  const isEditing = editingVacationWorker?.id === worker.id;
                  
                  return (
                    <div key={worker.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{worker.name || worker.email}</p>
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              if (isEditing) {
                                setEditingVacationWorker(null);
                              } else {
                                setEditingVacationWorker(worker);
                                setVacationTotalDays(totalDays);
                              }
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      
                      {isEditing && canEdit ? (
                        <div className="mt-2 p-2 bg-white rounded border">
                          <Label className="text-xs">Días totales de vacaciones ({vacationMonth.getFullYear()})</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              min="0"
                              max="365"
                              value={vacationTotalDays}
                              onChange={(e) => setVacationTotalDays(parseInt(e.target.value) || 0)}
                              className="w-20 h-8"
                            />
                            <Button size="sm" onClick={handleUpdateVacationDays}>
                              <Save className="h-3 w-3 mr-1" />
                              Guardar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingVacationWorker(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                              <div 
                                className="h-full bg-green-500 transition-all"
                                style={{ width: `${(approvedDays / totalDays) * 100}%` }}
                              />
                              <div 
                                className="h-full bg-yellow-400 transition-all"
                                style={{ width: `${(pendingDays / totalDays) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {approvedDays}✓ {pendingDays}⏳
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {availableDays} disponibles de {totalDays}
                            {rejectedDays > 0 && <span className="text-red-500 ml-1">({rejectedDays} rechazados)</span>}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
          
          {/* Solicitudes Pendientes */}
          {vacationDays.filter(d => d.status === 'pendiente').length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-orange-500" />
                    <CardTitle className="text-lg text-orange-700">Solicitudes Pendientes</CardTitle>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleBulkApprove(true)}>
                        <Check className="h-4 w-4 mr-1" />
                        Aprobar Todas
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkApprove(false)}>
                        <X className="h-4 w-4 mr-1" />
                        Rechazar Todas
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Trabajador</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Notas</TableHead>
                      <TableHead>Solicitado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vacationDays
                      .filter(d => d.status === 'pendiente')
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map(day => (
                        <TableRow key={day.id}>
                          <TableCell className="font-medium">{day.worker_name || day.worker_email}</TableCell>
                          <TableCell>{format(parseISO(day.date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{day.notes || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {day.created_at ? format(parseISO(day.created_at), 'dd/MM HH:mm') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && (
                              <div className="flex justify-end gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-green-600 hover:bg-green-100"
                                  onClick={() => handleApproveVacation(day.id, true)}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-red-600 hover:bg-red-100"
                                  onClick={() => {
                                    const reason = window.prompt('Motivo del rechazo (opcional):');
                                    if (reason !== null) {
                                      handleApproveVacation(day.id, false, reason);
                                    }
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          
          {/* Lista de días de vacaciones del trabajador seleccionado */}
          {selectedWorker && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Días de Vacaciones Asignados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {vacationDays
                    .filter(d => d.worker_id === selectedWorker)
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(day => (
                      <Badge 
                        key={day.id} 
                        variant="secondary" 
                        className={`flex items-center gap-1 ${
                          day.status === 'pendiente' ? 'bg-orange-100 text-orange-800' :
                          day.status === 'rechazado' ? 'bg-red-100 text-red-800' :
                          'bg-green-100 text-green-800'
                        }`}
                      >
                        {format(parseISO(day.date), 'dd MMM yyyy', { locale: es })}
                        {day.status === 'pendiente' && <span className="ml-1">(pendiente)</span>}
                        {day.status === 'rechazado' && <span className="ml-1">(rechazado)</span>}
                        <button onClick={() => handleDeleteVacationDay(day.id)}>
                          <Trash2 className="h-3 w-3 text-red-500 ml-1" />
                        </button>
                      </Badge>
                    ))}
                  {vacationDays.filter(d => d.worker_id === selectedWorker).length === 0 && (
                    <p className="text-sm text-muted-foreground">No hay días de vacaciones asignados</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* CONFIGURACIÓN TAB */}
        <TabsContent value="configuracion" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuración de Trabajadores</CardTitle>
              <CardDescription>
                Configura la comunidad autónoma y horario de cada trabajador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trabajador</TableHead>
                    <TableHead>Comunidad Autónoma</TableHead>
                    <TableHead>Horas/Día</TableHead>
                    <TableHead>Pausa Comida</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(worker => {
                    const schedule = schedules.find(s => s.worker_id === worker.id) || {};
                    const isEditing = editingSchedule === worker.id;
                    
                    return (
                      <TableRow key={worker.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{worker.name || 'Sin nombre'}</p>
                            <p className="text-sm text-muted-foreground">{worker.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select 
                              defaultValue={schedule.comunidad || "none"}
                              onValueChange={(v) => {
                                const newSchedule = {...schedule, comunidad: v === "none" ? "" : v};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Sin comunidad</SelectItem>
                                {COMUNIDADES.map(c => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">
                              {schedule.comunidad || 'Sin asignar'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input 
                              type="number" 
                              step="0.5"
                              min="1"
                              max="12"
                              className="w-20"
                              defaultValue={schedule.work_hours_per_day || 8}
                              onChange={(e) => {
                                const newSchedule = {...schedule, work_hours_per_day: parseFloat(e.target.value)};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            />
                          ) : (
                            <span>{schedule.work_hours_per_day || 8}h</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input 
                              type="number" 
                              step="15"
                              min="0"
                              max="120"
                              className="w-20"
                              defaultValue={schedule.lunch_break_minutes || 60}
                              onChange={(e) => {
                                const newSchedule = {...schedule, lunch_break_minutes: parseInt(e.target.value)};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            />
                          ) : (
                            <span>{schedule.lunch_break_minutes || 60} min</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => {
                                  const currentSchedule = schedules.find(s => s.worker_id === worker.id) || {};
                                  handleUpdateSchedule(worker.id, {
                                    comunidad: currentSchedule.comunidad || '',
                                    work_hours_per_day: currentSchedule.work_hours_per_day || 8,
                                    lunch_break_minutes: currentSchedule.lunch_break_minutes || 60
                                  });
                                }}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Guardar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingSchedule(null);
                                  fetchSchedules();
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditingSchedule(worker.id)}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No hay trabajadores configurados
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Editar Fichaje' : 'Nuevo Fichaje'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Trabajador</Label>
              <Select 
                value={entryForm.worker_id} 
                onValueChange={(v) => setEntryForm({...entryForm, worker_id: v})}
                disabled={!!editingEntry}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar trabajador" />
                </SelectTrigger>
                <SelectContent>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Fichaje</Label>
              <Select 
                value={entryForm.entry_type} 
                onValueChange={(v) => setEntryForm({...entryForm, entry_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.icon className="h-4 w-4" />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {entryForm.entry_type === 'incidencia' && (
              <div>
                <Label>Tipo de Incidencia</Label>
                <Select 
                  value={entryForm.incidencia_type} 
                  onValueChange={(v) => setEntryForm({...entryForm, incidencia_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCIDENCIA_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Fecha y Hora</Label>
              <Input 
                type="datetime-local"
                value={entryForm.timestamp}
                onChange={(e) => setEntryForm({...entryForm, timestamp: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Notas</Label>
              <Input 
                value={entryForm.notes}
                onChange={(e) => setEntryForm({...entryForm, notes: e.target.value})}
                placeholder="Notas opcionales..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateEntry}>
              {editingEntry ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Holiday Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Editar Festivo' : 'Nuevo Festivo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Fecha</Label>
              <Input 
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Nombre del Festivo</Label>
              <Input 
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                placeholder="Ej: Día de la Hispanidad"
              />
            </div>
            
            <div>
              <Label>Tipo</Label>
              <Select 
                value={holidayForm.type} 
                onValueChange={(v) => setHolidayForm({...holidayForm, type: v, comunidad: '', worker_id: ''})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">Nacional (todos los trabajadores)</SelectItem>
                  <SelectItem value="local">Local (por Comunidad Autónoma)</SelectItem>
                  <SelectItem value="personal">Personal (trabajador específico)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {holidayForm.type === 'local' && (
              <div>
                <Label>Comunidad Autónoma</Label>
                <Select 
                  value={holidayForm.comunidad} 
                  onValueChange={(v) => setHolidayForm({...holidayForm, comunidad: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar comunidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMUNIDADES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {holidayForm.type === 'personal' && (
              <div>
                <Label>Trabajador</Label>
                <Select 
                  value={holidayForm.worker_id} 
                  onValueChange={(v) => setHolidayForm({...holidayForm, worker_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar trabajador" />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateHoliday}>
              {editingHoliday ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTrackingTab;
